import { useCallback, useEffect, useRef } from 'react';

// Long enough to outlast the AntD collapse animation (motionDeadline: 500ms).
const SETTLE_MS = 900;
// Consecutive frames with an unchanged panel height before we call it settled.
const STABLE_FRAMES = 3;
// Trackpad inertia keeps firing 'wheel' for a while after the user lets go, so
// a click made right after a scroll used to kill the pin on frame 1. Ignore
// input for this long, then only react to a deliberate amount of scrolling.
const INPUT_GRACE_MS = 250;
const WHEEL_THRESHOLD_PX = 30;
// Keys that scroll the page. The Enter that opens a section from the keyboard is
// still propagating when the listeners below are added, so any other key would
// cancel the pin before its first frame.
const SCROLL_KEYS = new Set(['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' ', 'Spacebar']);
// The opening animation is over once the panel height has held still this long.
const ANCHOR_SETTLE_MS = 120;
// Upper bound on the anchoring suppression. A pause in growth is not the end of
// it: some panels fill in once their data arrives (the culture trees of the
// active substance and product fiches), well after the animation. Measured on
// such a panel: anchoring back on at 563 ms, data at 666 ms, header thrown from
// 212 px to 0.
const ANCHOR_MAX_MS = 10000;

// `overflow-anchor` lives on <html>, but the hook runs once per tab component
// (Traitement / Santé / Environnement are all mounted at once). Without a
// shared count, the first instance to finish would re-enable anchoring while
// another is still expanding a panel. Module scope is the right scope here:
// the property being guarded is itself global.
let anchorSuppressions = 0;

const suppressAnchoring = () => {
  anchorSuppressions += 1;
  document.documentElement.style.overflowAnchor = 'none';
};

const releaseAnchoring = () => {
  anchorSuppressions = Math.max(0, anchorSuppressions - 1);
  if (anchorSuppressions === 0) {
    document.documentElement.style.overflowAnchor = '';
  }
};

/**
 * Reveals a collapse section downward: pins its header below the sticky nav and
 * holds it there while the panel animates open, so the content always grows
 * beneath the title instead of shoving the page around.
 *
 * Two things make a section appear to open *upward*:
 *
 * 1. Chrome's scroll anchoring. As the panel grows, the browser picks an anchor
 *    node *below* the section and adds the inserted height to scrollY to keep it
 *    still — so the header slides off the top of the screen. Measured: scrollY
 *    grows by exactly the document's height delta. Suppressing `overflow-anchor`
 *    has to happen on the scrolling element; scoping it to the accordion does
 *    nothing, because the anchor node Chrome picks sits outside it.
 * 2. rc-collapse mounts panel content lazily and animates its height up from 0,
 *    so a single scroll fired at click time gets clamped to the *old*
 *    `scrollHeight - innerHeight` for sections near the bottom of the page.
 *    Hence the per-frame pin below rather than one scrollIntoView.
 *
 * The anchoring suppression runs on its own lifecycle, deliberately NOT undone
 * by `stop()`: `stop()` is what a user scroll triggers, and restoring anchoring
 * there would hand the page straight back to the behaviour we are suppressing.
 *
 * Nor is it undone when the height pauses, since a panel can keep growing once
 * its data loads. Anchoring comes back on a signal that nothing of ours is still
 * expanding, or that anchoring is needed again:
 * - the opened section is closed, or its tab hidden;
 * - another section is closed — keeping the reading position during a collapse
 *   is exactly what anchoring is for;
 * - the user scrolls deliberately once the opening animation is over;
 * - ANCHOR_MAX_MS elapses.
 *
 * Vertical placement comes from `scroll-margin-top` on the header (_collapse.scss),
 * so there is no offset hardcoded here.
 *
 * @returns {(sectionId: string) => void} Call with the anchor id of the section being opened.
 */
export const useRevealSectionDownward = () => {
  const rafRef = useRef(null);
  const cleanupRef = useRef(null);
  const anchorRafRef = useRef(null);
  const anchorCleanupRef = useRef(null);

  // Held so a given instance releases its own suppression exactly once, however
  // it ends: signalled, capped, re-armed or unmounted.
  const suppressedRef = useRef(false);

  const restoreAnchoring = useCallback(() => {
    if (anchorRafRef.current) {
      cancelAnimationFrame(anchorRafRef.current);
      anchorRafRef.current = null;
    }
    if (anchorCleanupRef.current) {
      anchorCleanupRef.current();
      anchorCleanupRef.current = null;
    }
    if (suppressedRef.current) {
      suppressedRef.current = false;
      releaseAnchoring();
    }
  }, []);

  const stop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      stop();
      restoreAnchoring();
    },
    [stop, restoreAnchoring],
  );

  return useCallback(
    (sectionId) => {
      stop();
      restoreAnchoring();

      const startedAt = performance.now();
      const pastGrace = () => performance.now() - startedAt >= INPUT_GRACE_MS;
      let lastHeight = -1;
      let stableFrames = 0;

      const findItem = () => document.getElementById(sectionId)?.closest('.ant-collapse-item') || null;
      const panelHeight = () => {
        const content = findItem()?.querySelector(':scope > .ant-collapse-content');
        return content ? content.getBoundingClientRect().height : -1;
      };

      // Hold scroll anchoring off for the whole growth, independently of the
      // pin: even when the user cancels the pin, the section still opens down.
      suppressedRef.current = true;
      suppressAnchoring();

      const opened = findItem();
      // Sections already open when this one opens. Any of them closing ends the
      // suppression on the next frame, before its collapse animation moves anything.
      const othersOpen = [...document.querySelectorAll('.ant-collapse-item-active')]
        .filter((item) => item !== opened && !item.contains(opened));

      let anchorLastHeight = -1;
      let stableSince = startedAt;
      // Deliberate scrolling only counts once the opening animation is over:
      // trackpad inertia past the grace period must not re-enable anchoring
      // while the panel is still expanding.
      let animationDone = false;
      let anchorWheelTravel = 0;
      const onAnchorWheel = (event) => {
        if (!animationDone || !pastGrace()) return;
        anchorWheelTravel += Math.abs(event.deltaY);
        if (anchorWheelTravel > WHEEL_THRESHOLD_PX) restoreAnchoring();
      };
      const onAnchorTouchMove = () => {
        if (animationDone && pastGrace()) restoreAnchoring();
      };
      const onAnchorKeyDown = (event) => {
        if (animationDone && SCROLL_KEYS.has(event.key)) restoreAnchoring();
      };
      window.addEventListener('wheel', onAnchorWheel, { passive: true });
      window.addEventListener('touchmove', onAnchorTouchMove, { passive: true });
      window.addEventListener('keydown', onAnchorKeyDown);
      anchorCleanupRef.current = () => {
        window.removeEventListener('wheel', onAnchorWheel);
        window.removeEventListener('touchmove', onAnchorTouchMove);
        window.removeEventListener('keydown', onAnchorKeyDown);
      };

      const watchAnchor = () => {
        const item = findItem();
        const openedGone = !item
          || !item.classList.contains('ant-collapse-item-active')
          || !item.getClientRects().length;
        const anotherClosed = othersOpen.some(
          (other) => !other.isConnected || !other.classList.contains('ant-collapse-item-active'),
        );
        const now = performance.now();
        if (openedGone || anotherClosed || now - startedAt > ANCHOR_MAX_MS) {
          restoreAnchoring();
          return;
        }
        const height = panelHeight();
        if (height !== anchorLastHeight) {
          anchorLastHeight = height;
          stableSince = now;
        }
        if (height > 0 && now - stableSince >= ANCHOR_SETTLE_MS) animationDone = true;
        anchorRafRef.current = requestAnimationFrame(watchAnchor);
      };
      anchorRafRef.current = requestAnimationFrame(watchAnchor);

      // The user always wins: a deliberate manual scroll hands control back.
      // Listening on 'wheel'/'touchmove'/'keydown' rather than 'scroll' so our
      // own scrolling below doesn't cancel the pin.
      let wheelTravel = 0;
      const onWheel = (event) => {
        if (!pastGrace()) return;
        wheelTravel += Math.abs(event.deltaY);
        if (wheelTravel > WHEEL_THRESHOLD_PX) stop();
      };
      const onTouchMove = () => {
        if (pastGrace()) stop();
      };
      const onKeyDown = (event) => {
        if (SCROLL_KEYS.has(event.key)) stop();
      };
      window.addEventListener('wheel', onWheel, { passive: true });
      window.addEventListener('touchmove', onTouchMove, { passive: true });
      window.addEventListener('keydown', onKeyDown);
      cleanupRef.current = () => {
        window.removeEventListener('wheel', onWheel);
        window.removeEventListener('touchmove', onTouchMove);
        window.removeEventListener('keydown', onKeyDown);
      };

      const tick = () => {
        const anchor = document.getElementById(sectionId);
        if (!anchor) {
          stop();
          return;
        }

        const header = anchor.closest('.ant-collapse-header') || anchor;
        header.scrollIntoView({ block: 'start', behavior: 'auto' });

        // The panel may not be mounted yet for the first few frames; keep
        // pinning until it appears and its height stops growing.
        const height = panelHeight();

        stableFrames = height === lastHeight ? stableFrames + 1 : 0;
        lastHeight = height;

        const settled = height > 0 && stableFrames >= STABLE_FRAMES;
        if (settled || performance.now() - startedAt > SETTLE_MS) {
          stop();
          return;
        }

        rafRef.current = requestAnimationFrame(tick);
      };

      rafRef.current = requestAnimationFrame(tick);
    },
    [stop, restoreAnchoring],
  );
};

export default useRevealSectionDownward;
